import type { FC } from 'react'
import { useState } from 'react'

import { Input, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import RefreshIcon from 'mdi-react/RefreshIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import authResources from '@/resources/auth'

const AccessCodeForm: FC<{
  phoneNumber: string
  countryId: number
}> = ({ phoneNumber, countryId }) => {
  const translate = useTranslation()

  const [isGenerateCodeButtonDisabled, setIsGenerateCodeButtonDisabled] = useState(false)
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  const generatePasswordMutation = useMutation(authResources.generatePassword)

  const handleGenerateCode = () => {
    generatePasswordMutation.mutate(
      { phoneNumber, countryId },
      {
        onSuccess() {
          setIsGenerateCodeButtonDisabled(true)

          setTimeout(() => {
            setIsGenerateCodeButtonDisabled(false)
          }, 5 * 1000)
        },
        onError() {
          setErrorPopupMessage({
            text: translate(
              "We couldn't generate code for this number. Please make sure the provided number is correct."
            ),
            title: translate('Code generation error'),
          })
        },
      }
    )
  }

  const hasPasswordBeenGenerated =
    generatePasswordMutation.isError || generatePasswordMutation.isSuccess

  return (
    <>
      <div className="send-code-button-container">
        <Button
          size="medium"
          className="mx-0 w-full"
          variant={hasPasswordBeenGenerated ? 'secondary' : 'primary'}
          Icon={hasPasswordBeenGenerated ? <RefreshIcon /> : undefined}
          disabled={isGenerateCodeButtonDisabled}
          isLoading={generatePasswordMutation.isLoading}
          onClick={handleGenerateCode}
        >
          {hasPasswordBeenGenerated ? (
            <Translation id="Try again" />
          ) : (
            <Translation id="Send code by SMS" />
          )}
        </Button>
      </div>

      <Field name="accessCode">
        {({ field, meta }: FieldProps) => (
          <Input
            isFullWidth
            capitalizeLabel
            id="accessCode"
            ariaLabel={translate('Code')}
            marginBottom={16}
            error={meta.error}
            touched={meta.touched}
            placeholder={translate('Enter your code')}
            {...field}
          />
        )}
      </Field>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
      />
    </>
  )
}

export default AccessCodeForm
